import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  runBackfillDiscovery,
  runBackfillApprove,
  runBackfillDrain,
  runBackfillPublish,
  resetStuckJobs,
  runCoverBackfill,
  getMissingCoverCount,
  rerollOverusedCovers,
} from "@/lib/editorial.functions";


import { getPipelineCounts } from "@/lib/dashboard.functions";
import { useRealtimeInvalidate } from "@/hooks/useRealtimeInvalidate";
import { WorkerPanel } from "@/components/admin/WorkerPanel";
import { PipelineStrip } from "@/components/admin/PipelineStrip";
import { EmbeddingsBackfillCard } from "@/components/admin/EmbeddingsBackfillCard";

export const Route = createFileRoute("/admin/backfill")({
  component: BackfillPage,
});

function BackfillPage() {
  const qc = useQueryClient();
  const [lookbackDays, setLookbackDays] = useState(60);
  const [targetCount, setTargetCount] = useState(120);
  const [perDay, setPerDay] = useState(2);
  const [minQuality, setMinQuality] = useState(55);
  const [lastDiscovery, setLastDiscovery] = useState<any>(null);

  const { data: counts } = useQuery({
    queryKey: ["admin", "pipeline-counts"],
    queryFn: () => getPipelineCounts(),
    refetchInterval: 5000,
  });
  useRealtimeInvalidate([
    { table: "article_ideas", queryKey: ["admin", "pipeline-counts"] },
    { table: "article_generation_tasks", queryKey: ["admin", "pipeline-counts"] },
    { table: "articles", queryKey: ["admin", "pipeline-counts"] },
  ]);

  const discover = useMutation({
    mutationFn: () => runBackfillDiscovery({ data: { lookbackDays, maxCandidates: Math.max(targetCount + 40, 160) } }),
    onSuccess: (r: any) => {
      setLastDiscovery(r);
      const tail = r.timedOut ? " (time budget hit — click again to continue)" : "";
      toast.success(`+${r.ideasCreated} ideas · ${r.duped} duped · ${r.scrapedEmpty} empty${tail}`);
    },
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "pipeline-counts"] }),
  });

  const approve = useMutation({
    mutationFn: () => runBackfillApprove({ data: { targetCount } }),
    onSuccess: (r: any) => toast.success(`Approved ${r.approved} · queued ${r.taskCount} draft tasks`),
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "pipeline-counts"] }),
  });

  const drain = useMutation({
    mutationFn: () => runBackfillDrain({ data: { maxTasks: 20, concurrency: 4 } }),
    onSuccess: (r: any) =>
      toast.success(`Processed ${r.processed} task(s)${r.idle ? " · queue empty" : ""}`),
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "pipeline-counts"] }),
  });

  const publish = useMutation({
    mutationFn: () => runBackfillPublish({ data: { targetCount, lookbackDays, perDay, minQuality } }),
    onSuccess: (r: any) =>
      toast.success(`Published ${r.published} backdated articles (${r.firstAt?.slice(0, 10)} → ${r.lastAt?.slice(0, 10)})`),
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "pipeline-counts"] }),
  });

  const reset = useMutation({
    mutationFn: () => resetStuckJobs(),
    onSuccess: (r: any) => toast.success(`Reset ${r.jobsFailed} stuck job(s), released ${r.tasksReleased} task(s)`),
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "pipeline-counts"] }),
  });

  const { data: coverStats } = useQuery({
    queryKey: ["admin", "missing-covers"],
    queryFn: () => getMissingCoverCount(),
    refetchInterval: 10000,
  });

  const coverBackfill = useMutation({
    mutationFn: () => runCoverBackfill({ data: { limit: 8 } }),
    onSuccess: (r: any) => {
      const errTail = r.errors?.length ? ` · errors: ${r.errors[0]}` : "";
      toast.success(`Covers: +${r.succeeded} ok · ${r.failed} failed · ${r.remaining} remaining${errTail}`);
    },
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "missing-covers"] }),
  });

  const rerollCovers = useMutation({
    mutationFn: () => rerollOverusedCovers({ data: { limit: 5 } }),
    onSuccess: (r: any) => {
      if (r.overUsed?.length === 0) {
        toast.success("Cover variety looks healthy — nothing over-quota right now");
      } else {
        const tail = r.errors?.length ? ` · errors: ${r.errors[0]}` : "";
        toast.success(`Re-rolled ${r.rerolled} (over-used: ${r.overUsed.join(", ")})${tail}`);
      }
    },
    onError: (e: any) => toast.error(e.message),
  });



  const pendingTasks = (counts?.tasks?.draftPending ?? 0) + (counts?.tasks?.editPending ?? 0) + (counts?.tasks?.running ?? 0);
  const newIdeas = counts?.ideas?.new ?? 0;
  const tooFewIdeas = newIdeas < 20;
  const draftsReady =
    (counts?.articles?.draft ?? 0) +
    (counts?.articles?.ready ?? 0) +
    (counts?.articles?.scheduled ?? 0);
  const publishCount = Math.min(targetCount, draftsReady);

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground">← back to dashboard</Link>
          <h1 className="headline-display text-3xl mt-2">Historical backfill</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Pull older stories from tier A–D sources, draft them, edit them, and publish
            them with backdated timestamps so the site looks like it's been running for months.
          </p>
        </div>
        <button
          onClick={() => reset.mutate()}
          disabled={reset.isPending}
          className="text-xs underline text-muted-foreground hover:text-foreground whitespace-nowrap"
        >
          {reset.isPending ? "Resetting…" : "Reset stuck jobs"}
        </button>
      </header>

      <PipelineStrip />

      <section>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
          Where things stand right now — click any cell to open its list
        </div>
        <div className="border border-border bg-paper-soft grid sm:grid-cols-3 lg:grid-cols-6 divide-x divide-y sm:divide-y-0 divide-border text-sm">
          <StatLink label="Ideas awaiting review" value={newIdeas} to="/admin/ideas" search={{ status: "new" }} />
          <StatLink label="Approved / queued" value={(counts?.ideas?.approved ?? 0) + (counts?.ideas?.queued ?? 0)} to="/admin/ideas" search={{ status: "approved" }} />
          <StatLink label="Writer/editor working" value={pendingTasks} to="/admin/generate" hint="background jobs" />
          <StatLink label="Drafts in progress" value={counts?.articles?.draft ?? 0} to="/admin/articles" search={{ filter: "draft" }} hint="writer done, editor pending" />
          <StatLink label="Ready + scheduled" value={(counts?.articles?.ready ?? 0) + (counts?.articles?.scheduled ?? 0)} to="/admin/articles" search={{ filter: "ready" }} hint="waiting for publisher" />
          <StatLink label="Published" value={counts?.articles?.published ?? 0} to="/admin/articles" search={{ filter: "published" }} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Total articles on the Articles tab = Drafts in progress + Ready + Scheduled + Published. Ideas are pre-articles — they become drafts once the writer runs.
        </p>
      </section>

      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <NumField label="Lookback days" value={lookbackDays} onChange={setLookbackDays} min={7} max={180} />
        <NumField label="Target articles" value={targetCount} onChange={setTargetCount} min={1} max={300} />
        <NumField label="Posts per day" value={perDay} onChange={setPerDay} min={1} max={10} />
        <NumField label="Min quality (publish)" value={minQuality} onChange={setMinQuality} min={0} max={100} />
      </section>

      <ol className="space-y-4">
        <Step
          n={1}
          title="Discover historical ideas"
          body="Parallel sweep (concurrency 6, 50s budget) across Google official, Sterling Sky, Whitespark, BrightLocal, Search Engine Land, Search Engine Roundtable archives, Reddit, and Local Search Forum. Click again to continue from where the time budget cut off."
          cta="Run deep discovery"
          pending={discover.isPending}
          onClick={() => discover.mutate()}
        />
        {lastDiscovery && (
          <li className="-mt-3 ml-12 text-xs text-muted-foreground tabular-nums">
            Last run: scanned {lastDiscovery.candidates} · {lastDiscovery.ideasCreated} new · {lastDiscovery.duped} duped · {lastDiscovery.scrapedEmpty} empty scrapes · {lastDiscovery.extractedNull} extract failures · {(lastDiscovery.durationMs / 1000).toFixed(1)}s{lastDiscovery.timedOut ? " · TIMED OUT (click again)" : ""}
          </li>
        )}
        <Step
          n={2}
          title="Auto-approve top ideas"
          body={`Approves the top ${targetCount} 'new' ideas (tier A > C > D, by novelty) and enqueues draft + edit tasks under a backfill job.${tooFewIdeas ? ` Need ≥20 new ideas first — currently ${newIdeas}. Run discovery a few more times.` : ""}`}
          cta={`Approve top ${targetCount}`}
          pending={approve.isPending}
          onClick={() => approve.mutate()}
          disabled={tooFewIdeas}
        />
        <Step
          n={3}
          title="Drain the queue (faster)"
          body={`Repeat until Queue hits 0. Each completed task adds to Drafts ready. pg_cron also drains ~1/min in the background. Click here to process up to 20 tasks at concurrency 4 within ~50s. ${pendingTasks > 0 ? `Currently ${pendingTasks} in queue · ${draftsReady} drafts ready.` : `Queue empty · ${draftsReady} drafts ready.`}`}
          cta="Drain 20 now"
          pending={drain.isPending}
          onClick={() => drain.mutate()}
          disabled={pendingTasks === 0}
        />
        <Step
          n={4}
          title="Publish backdated"
          body={`Publishes up to ${publishCount} of the ${draftsReady} drafts ready (quality ≥ ${minQuality}), spread across the last ${lookbackDays} days at ${perDay}/day, oldest source first. ${draftsReady === 0 ? "Drain the queue first — no drafts to publish." : ""}`}
          cta={draftsReady === 0 ? "No drafts ready" : `Publish ${publishCount} backdated`}
          pending={publish.isPending}
          onClick={() => publish.mutate()}
          disabled={draftsReady === 0}
        />
        <Step
          n={5}
          title="Generate missing cover images"
          body={`AI art-director picks an image type, palette, and medium per article (varying across the catalogue), then generates a 16:9 cover. Runs ~8 per click (image gateway is slow — ~90s). ${coverStats?.missing ?? 0} published article(s) still missing a cover.`}
          cta={coverStats?.missing ? `Generate 8 covers (${coverStats.missing} left)` : "All covers ready"}
          pending={coverBackfill.isPending}
          onClick={() => coverBackfill.mutate()}
          disabled={!coverStats?.missing}
        />
        <Step
          n={6}
          title="Re-roll over-used cover styles"
          body="If the recent covers are dominated by one image type (e.g. all editorial illustrations), this re-rolls the oldest 5 through the diversified art-director so the next batch picks different formats, palettes, and mediums."
          cta="Re-roll 5 over-used covers"
          pending={rerollCovers.isPending}
          onClick={() => rerollCovers.mutate()}
        />



      </ol>

      <EmbeddingsBackfillCard />

      <section>
        <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-2">Live worker</h2>
        <WorkerPanel />
      </section>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-medium mt-1">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function StatLink({
  label,
  value,
  to,
  search,
  hint,
}: {
  label: string;
  value: number;
  to: string;
  search?: Record<string, string>;
  hint?: string;
}) {
  return (
    <Link to={to} search={search as any} className="p-4 hover:bg-background transition-colors block">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight min-h-[24px]">{label}</div>
      <div className="text-2xl font-medium mt-1 tabular-nums">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </Link>
  );
}

function NumField({ label, value, onChange, min, max }: { label: string; value: number; onChange: (n: number) => void; min: number; max: number }) {
  return (
    <label className="block text-sm">
      <span className="text-xs uppercase tracking-wider text-muted-foreground block mb-1">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Math.max(min, Math.min(max, parseInt(e.target.value || "0", 10))))}
        className="w-full border border-border bg-background px-3 py-2"
      />
    </label>
  );
}

function Step({ n, title, body, cta, pending, onClick, disabled }: { n: number; title: string; body: string; cta: string; pending: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <li className="border border-border p-4 flex items-start gap-4">
      <div className="text-2xl text-muted-foreground tabular-nums">{n}</div>
      <div className="flex-1">
        <h3 className="font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground mt-1">{body}</p>
      </div>
      <button
        onClick={onClick}
        disabled={pending || disabled}
        className="bg-foreground text-background px-4 py-2 text-sm font-medium hover:bg-ochre-deep disabled:opacity-40 whitespace-nowrap"
      >
        {pending ? "Working…" : cta}
      </button>
    </li>
  );
}
